import { ATRIBUTOS, type Pontos } from '../escala'
import { TACOS_GRATUITOS } from '../gratuitos'
import { distribuir, pontosDoTaco } from '../pontos'
import { RULESET_V3, type PerfilDeTaco, type Raridade } from '../ruleset'
import type { LinhaDaTabela } from './tipos'

/**
 * Como raridade + arquétipo viram pontos numa coleção parceira. Lista
 * FECHADA e calculada no servidor: quem cadastra escolhe o modelo, nunca os
 * números.
 *
 * - `ruleset-v3`: o mesmo orçamento dos nossos tacos; ninguém domina ninguém.
 * - `piso-do-gratuito`: o modelo que o Bunker desenhou para a GDM. O gratuito
 *   é o piso e a raridade SOMA um extra; lendário é 100 em tudo. Todo taco da
 *   coleção fica melhor que o gratuito em tudo — o painel de aprovação avisa.
 * - `por-traits`: o cálculo SEM CLASSE do Bunker (mrBunker23, PR #27). O
 *   gratuito também é o piso, mas quem soma não é a raridade: é cada TRAIT,
 *   pela raridade dele dentro da coleção. Mesmo aviso na aprovação.
 */
export type ModeloDePontos = 'ruleset-v3' | 'piso-do-gratuito' | 'por-traits'
export const MODELOS_DE_PONTOS = ['ruleset-v3', 'piso-do-gratuito', 'por-traits'] as const satisfies readonly ModeloDePontos[]
export const modeloValido = (v: unknown): v is ModeloDePontos => (MODELOS_DE_PONTOS as readonly unknown[]).includes(v)

export const EXTRA_DO_PISO: Record<Exclude<Raridade, 'legendary'>, number> = { gratuito: 0, common: 20, uncommon: 40, rare: 60, epic: 90 }
const FULL: Pontos = { peso: 100, efeito: 100, aderencia: 100, quebra: 100 }

/** O piso dos modelos que partem do grátis: o taco que todo jogador já tem. */
const pisoDoGratuito = (): Pontos => pontosDoTaco(TACOS_GRATUITOS.padrao, RULESET_V3)

/**
 * O piso de onde a coleção parte, atributo por atributo.
 *
 * O grátis é o MÍNIMO de todos: uma coleção pode pedir um piso mais alto (a
 * GDM pede 50 em tudo, decisão do Marcos no #33 — com o piso no grátis os
 * tacos de traits comuns saíam sem quebra nenhuma), mas nenhuma pode pedir um
 * taco pior que o que todo jogador já tem de graça. Sem piso configurado, o
 * grátis segue sendo o piso e nada muda.
 */
const pisoDaColecao = (piso?: Pontos): Pontos => {
  const fora = pisoDoGratuito()
  if (!piso) return fora
  for (const k of ATRIBUTOS) fora[k] = Math.min(RULESET_V3.tetoPorAtributo, Math.max(fora[k], piso[k]))
  return fora
}

/**
 * Para onde uma categoria de trait manda os seus pontos no `por-traits`: um
 * dos quatro atributos, ou `todos` — o papel que o "Macaco" (a cor do bicho)
 * tinha no código do Bunker, a base que sobe um pouco em tudo.
 */
export type AlvoDeTrait = keyof Pontos | 'todos'
export const ALVOS_DE_TRAIT = [...ATRIBUTOS, 'todos'] as const satisfies readonly AlvoDeTrait[]
export const alvoDeTraitValido = (v: unknown): v is AlvoDeTrait => (ALVOS_DE_TRAIT as readonly unknown[]).includes(v)

/** O máximo que um trait raríssimo soma no atributo dele. */
export const BONUS_DO_TRAIT = 40
/** O máximo que a categoria `todos` soma em CADA atributo. */
export const BONUS_DA_BASE = 12
/**
 * A escala do log da raridade de um trait, preservada do código do Bunker
 * (o tamanho da GDM). É uma ESCALA, não o tamanho da coleção: pontos de
 * coleções diferentes têm de ser comparáveis na mesa, e um trait único numa
 * coleção de 50 não pode valer o mesmo que um trait único numa de 5000.
 * Abaixo de 1/500 o valor satura no bônus inteiro.
 */
export const TOTAL_DE_REFERENCIA = 500

/**
 * Quão raro é um trait, de 0 (todo mundo tem) a 1 (um em 500 ou menos).
 *
 * Em LOG, e não linear: a diferença entre 1% e 2% é enorme numa coleção, e a
 * entre 50% e 51% não é nenhuma. Linear, quase todo trait raro viraria o
 * máximo e os comuns ficariam todos iguais.
 */
export function raridadeDoTrait(frequencia: number): number {
  if (!(frequencia > 0)) return 0
  return Math.min(1, Math.max(0, Math.log(1 / frequencia) / Math.log(TOTAL_DE_REFERENCIA)))
}

/**
 * O que o `por-traits` precisa e o perfil não tem: as frequências dos traits
 * desta NFT, por ALVO (`peso`…`quebra` ou `todos` — o montador já resolveu a
 * categoria pelo `atributoDaCategoria` das regras), e se ela é a peça única.
 *
 * Tudo opcional: sem extra, o `por-traits` cai no piso. Quem impede uma
 * tabela `por-traits` sem traits é o servidor, no `/rascunho`.
 */
export type ExtraDePontos = { traits?: Record<string, number>; especial?: boolean }

/**
 * O método do Bunker, generalizado: o grátis é o piso, a categoria `todos`
 * soma até `BONUS_DA_BASE` em tudo, cada atributo ganha até `BONUS_DO_TRAIT`
 * pelo trait que o alimenta, a peça única nasce full, e o teto é o da física.
 */
function pontosPorTraits(extra: ExtraDePontos | undefined, piso?: Pontos): Pontos {
  if (extra?.especial) return { ...FULL }
  const fora = pisoDaColecao(piso)
  // `Object.hasOwn`: o objeto vem de JSON, e `toString`/`constructor` herdados
  // não são traits desta NFT.
  const freq = (k: string): number => (extra?.traits && Object.hasOwn(extra.traits, k) ? Number(extra.traits[k]) : 0)
  const base = Math.round(BONUS_DA_BASE * raridadeDoTrait(freq('todos')))
  for (const k of ATRIBUTOS) fora[k] += base + Math.round(BONUS_DO_TRAIT * raridadeDoTrait(freq(k)))
  for (const k of ATRIBUTOS) fora[k] = Math.min(RULESET_V3.tetoPorAtributo, fora[k])
  return fora
}

/**
 * O `extra` de uma linha da tabela, do jeito que o modelo espera.
 *
 * Existe para não haver duas montagens: o jogo (`tacoDeParceira`) e a tela do
 * admin (`/nfts`) precisam dos MESMOS pontos, e foi justamente esquecer isto
 * numa delas que fez o painel mostrar o taco grátis em toda NFT.
 * `exactOptionalPropertyTypes` não deixa passar `undefined`, daí os ifs.
 */
export function extraDaLinha(l: { traits?: Record<string, number>; especial?: boolean }): ExtraDePontos {
  const fora: ExtraDePontos = {}
  if (l.traits) fora.traits = l.traits
  if (l.especial) fora.especial = true
  return fora
}

export function pontosDoModelo(modelo: ModeloDePontos, perfil: PerfilDeTaco, extra?: ExtraDePontos, pisoDaRegra?: Pontos): Pontos {
  if (modelo === 'ruleset-v3') return pontosDoTaco(perfil, RULESET_V3)
  if (modelo === 'por-traits') return pontosPorTraits(extra, pisoDaRegra)
  if (perfil.raridade === 'legendary') return { ...FULL }
  const piso = pisoDaColecao(pisoDaRegra)
  const extraDaRaridade = distribuir(EXTRA_DO_PISO[perfil.raridade], RULESET_V3.perfil[perfil.arquetipo], RULESET_V3.tetoPorAtributo)
  const fora = { ...piso }
  for (const k of ATRIBUTOS) fora[k] = Math.min(RULESET_V3.tetoPorAtributo, piso[k] + extraDaRaridade[k])
  return fora
}

/** Seed = número + 1: a seed 0 quer dizer "sem variação". */
export const perfilDaLinha = (l: Pick<LinhaDaTabela, 'numero' | 'raridade' | 'arquetipo'>): PerfilDeTaco => ({
  raridade: l.raridade,
  arquetipo: l.arquetipo,
  seed: l.numero + 1,
  geracao: 0,
})
